<!-- Modal for creating and editing WaterMeterBoxes -->
<!-- Copy right - © 2025 tecera gmbh -->

<script>
    import { fly, fade } from "svelte/transition";
    import {
        all_compound_water_meters,
        modal_mode,
        selected_device,
        toast_message,
        all_water_meter_boxes,
    } from "../../stores.js";
    import { BASE_API_URL } from "../../config.js";
    import ModalInputField from "./form_parts/ModalInputField.svelte";
    import KoordinatenSetzen from "./form_parts/KoordinatenSetzen.svelte";
    import ToggleButton from "../buttons/ToggleButton.svelte";
    import {
        get_free_water_meters,
        handle_bad_response,
    } from "../../scripts/data.js";
    import ConfirmModal from "./ConfirmModal.svelte";

    export let modals;
    export let open_modal;

    $: is_open = open_modal === modals.WATER_METER_BOX;

    // data for fields
    let title;
    let device_name = null;
    let city = null;
    let ortsteil = null;
    let strasse = null;
    let hausnummer = null;
    let longitude = null;
    let latitude = null;
    let ergaenzung = null;
    let bemerkung_lage = null;

    function clear_inputs() {
        device_name = null;
        city = null;
        ortsteil = null;
        strasse = null;
        hausnummer = null;
        longitude = null;
        latitude = null;
        ergaenzung = null;
        bemerkung_lage = null;
    }

    // modal input fields
    let device_name_field = {
        id: "device_name",
        placeholder: "Bezeichnung",
        label: "Bezeichnung",
        max_length: 64,
    };

    let city_field = {
        id: "city",
        placeholder: "Stadt",
        label: "Stadt",
        max_length: 64,
    };

    let ortsteil_field = {
        id: "ortsteil",
        placeholder: "Ortsteil",
        label: "Ortsteil",
        max_length: 64,
    };

    let strasse_field = {
        id: "strasse",
        placeholder: "Straße",
        label: "Straße",
        max_length: 64,
    };

    let hausnummer_field = {
        id: "hausnummer",
        placeholder: "Hausnummer",
        label: "Hausnummer",
        max_length: 10,
    };

    let device_active = true; // deviceconfig active status. true by default, but changed to the value of the actual config object
    let allow_gps_coordinates = true; // allow the device to set the coordinates
    let confirm_open = false; // whether the confirm modal is open
    let confirm_title =
        "Sind Sie sicher, dass Sie dieses Gerät löschen möchten?"; // title for the confirm modal
    let setManCoords = false; // if false the coordinates are created automiatcally
    let is_dropdown_open = false; // determines whether the dropdown is open or not
    let is_dropdown_box_open = false; // determines whether the dropdown is open or not
    let can_close = false; // determines whether the modal can be closed

    // reactivity to open and close the modal with delay
    $: {
        if (is_open) {
            document.body.style.overflow = "hidden";
            document.body.classList.add("modal-open");
            setTimeout(() => {
                can_close = true; // 250ms delay to prevent closing the modal immediately
            }, 250);
        } else {
            setTimeout(() => {
                document.body.style.overflow = "auto";
                document.body.classList.remove("modal-open");
            }, 350); // enable scrolling after the modal is closed
        }
    }

    function close_modal() {
        if (can_close) {
            open_modal = modals.NONE; // reset the open modal to none
            $modal_mode = "closed";
            can_close = false;
        }
    }

    let is_button_disabled = false;

    function timeout_button() {
        is_button_disabled = true;
        setTimeout(() => {
            is_button_disabled = false;
        }, 500);
    }

    // get pre-filled data for the form
    function get_form_data() {
        title = "Box editieren";
        device_name = $selected_device.name || "unbennantes Gerät";
        city = $selected_device.location;
        ortsteil = $selected_device.district;
        strasse = $selected_device.street;
        hausnummer = $selected_device.house_number;
        longitude = $selected_device.longitude;
        latitude = $selected_device.latitude;
        ergaenzung = $selected_device.comment;
        bemerkung_lage = $selected_device.comment_location;
        device_active = $selected_device.status === "active" ? true : false;
        allow_gps_coordinates =
            $selected_device.allow_gps_coordinates === 1 ? true : false;
        setManCoords = false;
    }

    // reactivity to fetch correct data or clear inputs
    $: if (is_open) {
        get_form_data();
    }

    /**
     *
     * Funktion ermittelt die Koordinaten der genannten Addresse mit Hilfe von Nominatim von OSM
     * Falls Koordinaten nicht ermittelt werden können, werden null Werte zurückgegeben
     *
     */
    async function get_coords(address) {
        let url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${address}`;
        try {
            let response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching coordinates`);
            }
            let data = await response.json();
            latitude = data[0].lat;
            longitude = data[0].lon;
            return [latitude, longitude];
        } catch (error) {
            latitude = null;
            longitude = null;
            return [latitude, longitude];
        }
    }

    // updating the device when editing. Box or compound water meter
    async function update_device() {
        timeout_button();

        if (!device_name) {
            alert("Bitte Bezeichnung eingeben.");
            return;
        }
        // if (!setManCoords) {
        //     await set_coords();
        // }
        let device_active_string = device_active ? "active" : "inactive";
        let device_config_id = $selected_device.id;
        let allow_gps_coordinates_int = allow_gps_coordinates ? 1 : 0;
        let device_data = {
            name: device_name,
            location: city,
            district: ortsteil,
            street: strasse,
            house_number: hausnummer,
            longitude: parseFloat(longitude),
            latitude: parseFloat(latitude),
            comment: ergaenzung,
            comment_location: bemerkung_lage,
            status: device_active_string,
            allow_gps_coordinates: allow_gps_coordinates_int,
        };
        let url_extension = `/water_meter_boxes/${device_config_id}/`;
        let request_url = BASE_API_URL + url_extension;

        try {
            const response = await fetch(request_url, {
                method: "PUT",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(device_data),
            });
            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }
            let data = await response.json();

            let idToFind = data.data.id;
            let index = $all_water_meter_boxes.findIndex(
                (device_config) => device_config.id == idToFind,
            );
            $all_water_meter_boxes[index] = data.data;
            $selected_device = data.data;
            close_modal();
            $toast_message = " Gerät erfolgreich editiert";

            return true;
        } catch (error) {
            return false;
        }
    }

    // delete request to the API
    async function delete_device() {
        timeout_button();

        let device_config_id = $selected_device.id;

        let url_extension = `/hymo_configs/${device_config_id}`;
        let request_url = BASE_API_URL + url_extension;
        try {
            const response = await fetch(request_url, {
                method: "DELETE",
                headers: {
                    Authorization: `Token ${sessionStorage.getItem(
                        "2kai_access_token",
                    )}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                handle_bad_response(response);
                return false;
            }

            close_modal();
            get_free_water_meters();
            $toast_message = " Gerät erfolgreich gelöscht";

            let idToFind = device_config_id;
            let index = $all_compound_water_meters.findIndex(
                (device_config) => device_config.id == idToFind,
            );
            if (index !== -1) {
                $all_compound_water_meters.splice(index, 1);
            }
            $all_compound_water_meters = $all_compound_water_meters;
            $selected_device = $all_compound_water_meters[0];

            return true;
        } catch (error) {
            return false;
        }
    }

    async function set_coords() {
        // Zusammengesetzte Addresse aufbauen
        let ort = city ? city : "";
        let str = strasse ? strasse : "";
        let nmr = hausnummer ? hausnummer : "";

        var full_address = ort + " " + str + " " + nmr;

        // Koordinaten von Nominatim abfragen
        var coords = await get_coords(full_address);

        // Koordniaten in vorgesehene Felder schreiben, damit sie beim Speichern in die DB übernommen werden
        if (coords.length !== 0) {
            latitude = coords[0];
            longitude = coords[1];
        }
        // Sollten keine Koordinaten geliefert werden --> Alarm ausgeben
        else {
            alert("Addresse konnte nicht gefunden werden");
        }
    }

    function handleMenuOpen() {
        if (is_dropdown_open) {
            is_dropdown_open = false;
            document.body.removeEventListener("click", handleMenuClose);
        } else {
            if (is_dropdown_box_open) {
                handleMenuCloseBox();
            }
            is_dropdown_open = true;
            document.body.addEventListener("click", handleMenuClose);
        }
    }

    function handleMenuCloseBox() {
        is_dropdown_box_open = false;
        document.body.removeEventListener("click", handleMenuClose);
    }
    function handleMenuClose() {
        is_dropdown_open = false;
        document.body.removeEventListener("click", handleMenuClose);
    }
</script>

{#if is_open}
    <div
        class="svelte-modal svelte-backdrop"
        tabindex="-1"
        role="dialog"
        on:mousedown|self={() => close_modal()}
        on:keydown={(e) => {
            if (e.key === "Escape") {
                close_modal();
            }
        }}
        transition:fade={{ duration: 150 }}
    >
        <div
            class="modal-dialog modal-dialog-centered modal-dialog-wide"
            role="document"
            in:fly={{ y: -400, duration: 350, delay: 100 }}
            out:fly={{ y: -400, duration: 350 }}
        >
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="deleteTilte">
                        {title}
                    </h5>
                    <button
                        type="button"
                        class="close"
                        on:click={() => close_modal()}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body" id="edit">
                    <div class="mb-0 mt-2">
                        <h2
                            class="p-h2 mb-2"
                            style="color:#009FDF; margin: 0 auto; text-align: center; display: block; width: fit-content;"
                        >
                            {$selected_device.box_id} - {device_name}
                        </h2>

                        <ModalInputField
                            field_data={device_name_field}
                            bind:input_value={device_name}
                        />
                        <div class="form-row">
                            <ModalInputField
                                field_data={city_field}
                                bind:input_value={city}
                                col_class="col-md-6"
                            />
                            <ModalInputField
                                field_data={ortsteil_field}
                                bind:input_value={ortsteil}
                                col_class="col-md-6"
                            />
                        </div>
                        <div class="form-row">
                            <ModalInputField
                                field_data={strasse_field}
                                bind:input_value={strasse}
                                col_class="col-md-9"
                            />
                            <ModalInputField
                                field_data={hausnummer_field}
                                bind:input_value={hausnummer}
                                col_class="col-md-3"
                            />
                        </div>

                        <div class="form-group">
                            <label for="comment_field">Ergänzung</label>
                            <textarea
                                class="form-control"
                                id="comment_field"
                                rows="3"
                                maxlength="256"
                                bind:value={ergaenzung}
                                placeholder="Ergänzung..."
                            ></textarea>
                        </div>
                        <div class="form-group">
                            <label for="comment_location_field"
                                >Bemerkung zur Lage</label
                            >
                            <textarea
                                class="form-control"
                                id="comment_location_field"
                                rows="3"
                                maxlength="256"
                                bind:value={bemerkung_lage}
                                placeholder="Bemerkung zur Lage..."
                            ></textarea>
                        </div>

                        <ToggleButton
                            id="aktiv"
                            label="Aktiv"
                            bind:toggle={device_active}
                            classes={"mt-2"}
                        />
                        <KoordinatenSetzen
                            classes={"mt-2"}
                            bind:longitude
                            bind:latitude
                            bind:setManCoords
                            {set_coords}
                        />

                        <ToggleButton
                            id="auto-coords"
                            label="Automatische GPS Positionierung"
                            bind:toggle={allow_gps_coordinates}
                            classes={"mt-2"}
                        />
                    </div>
                </div>
                <!-- Modal Footer -->
                <div class="modal-footer mt-2">
                    <!-- {#if $modal_mode == "edit"}
                        <button
                            type="button"
                            class="btn btn-danger mr-auto"
                            disabled={is_button_disabled}
                            on:click={() => (confirm_open = true)}
                            >Löschen</button
                        >
                    {/if} -->
                    <button
                        type="button"
                        class="btn btn-secondary"
                        on:click={() => close_modal()}>Abbrechen</button
                    >

                    <button
                        type="button"
                        class="btn btn-primary"
                        disabled={is_button_disabled}
                        on:click={() => update_device()}>Speichern</button
                    >
                </div>
            </div>
        </div>
    </div>
    <ConfirmModal
        bind:show_modal={confirm_open}
        title={confirm_title}
        on:confirm={() => delete_device()}
    />
{/if}
